import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationSettingsService } from '@services';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import { showToast } from '@utils/toast';
import type { NotificationConfigValue } from '@app-types';
import NotificationSidebar from './NotificationSidebar';
import NotificationEditor from './NotificationEditor';
import styles from './NotificationSettingsTab.module.css';

const NotificationSettingsTab: React.FC = () => {
  const queryClient = useQueryClient();

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // Deferred notification to switch to once the discard-changes prompt is
  // confirmed; null means no pending switch. Mirrors EmailTemplatesPage.
  const [pendingSwitchCode, setPendingSwitchCode] = useState<string | null>(null);

  // Fetch all notifications
  const { data: notifications, isLoading: notificationsLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => notificationSettingsService.getNotifications()
  });

  // Fetch the selected notification's details
  const { data: selectedNotification } = useQuery({
    queryKey: ['notification-setting', selectedCode],
    queryFn: () => notificationSettingsService.getNotification(selectedCode!),
    enabled: !!selectedCode
  });

  const updateMutation = useMutation({
    mutationFn: ({ code, config }: { code: string; config: Record<string, NotificationConfigValue> }) =>
      notificationSettingsService.updateNotification(code, config),
    onSuccess: () => {
      showToast.success('Notification settings saved');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      queryClient.invalidateQueries({ queryKey: ['notification-setting', selectedCode] });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to save: ${error.message}`);
    }
  });

  const resetMutation = useMutation({
    mutationFn: (code: string) => notificationSettingsService.resetNotification(code),
    onSuccess: () => {
      showToast.success('Notification reset to default');
      setHasChanges(false);
      setShowResetConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      queryClient.invalidateQueries({ queryKey: ['notification-setting', selectedCode] });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to reset: ${error.message}`);
    }
  });

  // Select the first notification once the list loads
  useEffect(() => {
    if (notifications && notifications.length > 0 && !selectedCode) {
      setSelectedCode(notifications[0].code);
    }
  }, [notifications, selectedCode]);

  const handleSelectNotification = useCallback(
    (code: string) => {
      if (hasChanges) {
        // Defer the switch: surface a confirmation modal instead of the native
        // window.confirm so the discard prompt matches the rest of the app.
        setPendingSwitchCode(code);
        return;
      }
      setSelectedCode(code);
    },
    [hasChanges]
  );

  // Confirm discarding unsaved changes and switch to the pending notification.
  const handleConfirmSwitch = useCallback(() => {
    if (pendingSwitchCode) {
      setSelectedCode(pendingSwitchCode);
    }
    setPendingSwitchCode(null);
  }, [pendingSwitchCode]);

  const handleSave = useCallback(
    (config: Record<string, NotificationConfigValue>) => {
      if (!selectedCode) return;
      updateMutation.mutate({ code: selectedCode, config });
    },
    [selectedCode, updateMutation]
  );

  const handleReset = useCallback(() => {
    if (!selectedCode) return;
    resetMutation.mutate(selectedCode);
  }, [selectedCode, resetMutation]);

  if (notificationsLoading) {
    return <div className={styles.loadingState}>Loading notifications...</div>;
  }

  return (
    <div className={styles.settingsTab} data-test-id="notification-settings-tab">
      <div className={styles.settingsLayout}>
        <NotificationSidebar
          notifications={notifications || []}
          selectedCode={selectedCode}
          hasChanges={hasChanges}
          onSelectNotification={handleSelectNotification}
        />

        <NotificationEditor
          notification={selectedNotification}
          isSaving={updateMutation.isPending}
          isResetting={resetMutation.isPending}
          onSave={handleSave}
          onResetClick={() => setShowResetConfirm(true)}
          onDirtyChange={setHasChanges}
        />
      </div>

      <ConfirmationModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleReset}
        title="Reset Notification to Default?"
        message="This will discard all overrides for this notification and restore its default settings. This action cannot be undone."
        confirmLabel="Reset to Default"
        confirmVariant="danger"
        isProcessing={resetMutation.isPending}
        dataTestId="reset-confirmation-modal"
      />

      {/* Discard-changes confirmation when switching notifications */}
      <ConfirmationModal
        isOpen={pendingSwitchCode !== null}
        onClose={() => setPendingSwitchCode(null)}
        onConfirm={handleConfirmSwitch}
        title="Discard unsaved changes?"
        message="You have unsaved changes. Switching notifications will discard them. This action cannot be undone."
        confirmLabel="Discard & Switch"
        confirmVariant="danger"
        dataTestId="switch-notification-confirmation-modal"
      />
    </div>
  );
};

export default NotificationSettingsTab;
